import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { Plus, Edit, Trash2, Check, Minus } from 'lucide-react';
import { toast } from 'react-hot-toast';
import DataTable from '../DataTable';
import LeadSourceModal from './LeadSourceModal';
import ConfirmDialog from '../ConfirmDialog';
import IconButton from '../common/IconButton';
import SmartSearchBar from '../leads/SmartSearchBar';
import { LeadSourceDto, CreateLeadSourceDto, UpdateLeadSourceDto, leadSourcesApi } from '../../services/settingsService';
import { handleApiError } from '../../utils/errorHandler';

const TabHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  gap: 16px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchWrapper = styled.div`
  flex: 1;
  max-width: 400px;

  @media (max-width: 767px) {
    max-width: 100%;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: var(--primary-color);
          color: white;
          border: 1px solid var(--primary-color);
          &:hover {
            background: transparent;
            color: var(--primary-color);
          }
        `;
      case 'danger':
        return `
          background: var(--error-color);
          color: white;
          &:hover { background: #d32f2f; }
        `;
      default:
        return `
          background: var(--surface-color);
          color: var(--text-primary);
          border: 1px solid var(--border-color);
          &:hover { background: var(--hover-background); }
        `;
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CenteredCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const TextCell = styled.span`
  font-size: 14px;
  color: var(--text-primary);
`;

const SecondaryText = styled.span`
  font-size: 13px;
  color: var(--text-secondary);
`;

const ActionsCell = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  justify-content: center;
`;

const LeadSourcesTab: React.FC = () => {
  const [leadSources, setLeadSources] = useState<LeadSourceDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedLeadSource, setSelectedLeadSource] = useState<LeadSourceDto | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [leadSourceToDelete, setLeadSourceToDelete] = useState<LeadSourceDto | null>(null);

  useEffect(() => {
    loadLeadSources();
  }, []);

  const loadLeadSources = async () => {
    try {
      setLoading(true);
      const data = await leadSourcesApi.getAll();
      setLeadSources(data);
    } catch (error) {
      toast.error(handleApiError(error, 'שגיאה בטעינת מקורות ליד'));
    } finally {
      setLoading(false);
    }
  };

  // Filter lead sources based on search
  const filteredLeadSources = useMemo(() => {
    if (!searchTerm) return leadSources;
    const search = searchTerm.toLowerCase();
    return leadSources.filter(source =>
      source.name.toLowerCase().includes(search) ||
      source.description?.toLowerCase().includes(search)
    );
  }, [leadSources, searchTerm]);

  const handleAddLeadSource = () => {
    setSelectedLeadSource(null);
    setModalOpen(true);
  };

  const handleEditLeadSource = (leadSource: LeadSourceDto) => {
    setSelectedLeadSource(leadSource);
    setModalOpen(true);
  };

  const handleDeleteLeadSource = (leadSource: LeadSourceDto) => {
    setLeadSourceToDelete(leadSource);
    setDeleteConfirmOpen(true);
  };

  const handleSaveLeadSource = async (leadSourceData: CreateLeadSourceDto | UpdateLeadSourceDto) => {
    try {
      if (selectedLeadSource) {
        await leadSourcesApi.update(selectedLeadSource.id, leadSourceData as UpdateLeadSourceDto);
      } else {
        await leadSourcesApi.create(leadSourceData as CreateLeadSourceDto);
      }
      await loadLeadSources();
    } catch (error) {
      console.error('שגיאה בשמירת מקור ליד:', error);
      throw error;
    }
  };

  const confirmDelete = async () => {
    if (!leadSourceToDelete) return;

    try {
      await leadSourcesApi.delete(leadSourceToDelete.id);
      await loadLeadSources();
      setDeleteConfirmOpen(false);
      setLeadSourceToDelete(null);
    } catch (error) {
      toast.error(handleApiError(error, 'שגיאה במחיקת מקור ליד'));
    }
  };

  const columns = [
    {
      key: 'name',
      title: 'שם המקור',
      width: '200px',
      render: (value: string) => (
        <CenteredCell>
          <TextCell>{value}</TextCell>
        </CenteredCell>
      )
    },
    {
      key: 'description',
      title: 'תיאור',
      width: '250px',
      render: (value: string) => (
        <CenteredCell>
          <SecondaryText>{value || '-'}</SecondaryText>
        </CenteredCell>
      )
    },
    {
      key: 'leadsCount',
      title: 'לידים החודש',
      width: '120px',
      render: (value: number) => (
        <CenteredCell>
          <TextCell>{value}</TextCell>
        </CenteredCell>
      )
    },
    {
      key: 'successRate',
      title: 'אחוז הצלחה',
      width: '120px',
      render: (value: number) => (
        <CenteredCell>
          <TextCell>{value.toFixed(1)}%</TextCell>
        </CenteredCell>
      )
    },
    {
      key: 'isActive',
      title: 'סטטוס',
      width: '80px',
      render: (value: boolean) => (
        <CenteredCell>
          {value ? (
            <Check size={14} color="#16a34a" strokeWidth={2.5} />
          ) : (
            <Minus size={14} color="#d1d5db" />
          )}
        </CenteredCell>
      )
    },
    {
      key: 'actions',
      title: 'פעולות',
      width: '100px',
      render: (_: any, leadSource: LeadSourceDto) => (
        <ActionsCell>
          <IconButton
            icon={Edit}
            onClick={() => handleEditLeadSource(leadSource)}
            title="עריכה"
          />
          <IconButton
            icon={Trash2}
            onClick={() => handleDeleteLeadSource(leadSource)}
            title="מחיקה"
            variant="danger"
          />
        </ActionsCell>
      )
    }
  ];

  return (
    <>
      <TabHeader>
        <SearchWrapper>
          <SmartSearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="חיפוש מקורות ליד..."
          />
        </SearchWrapper>
        <Button variant="primary" onClick={handleAddLeadSource}>
          <Plus size={16} />
          מקור ליד חדש
        </Button>
      </TabHeader>

      <DataTable
        data={filteredLeadSources}
        columns={columns}
        loading={loading}
      />

      <LeadSourceModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSaveLeadSource}
        leadSource={selectedLeadSource}
        title={selectedLeadSource ? 'עריכת מקור ליד' : 'הוספת מקור ליד חדש'}
      />

      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={confirmDelete}
        title="מחיקת מקור ליד"
        message={`האם אתה בטוח שברצונך למחוק את מקור הליד "${leadSourceToDelete?.name}"?`}
        type="danger"
      />
    </>
  );
};

export default LeadSourcesTab;
